import { useMutation, useQueryClient } from '@tanstack/react-query';
import { projectService } from '@services/project';
import { toast } from 'react-toastify';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { Project } from '@models/project.model';
import { PROJECTS_QUERY_KEY } from './useProjects';
import { PROJECT_QUERY_KEY } from './useProject';

// Toggles a project favorite with an optimistic update on both the dashboard rail
// cache entry ([PROJECTS_QUERY_KEY, userId]) and the single-project detail cache
// ([PROJECT_QUERY_KEY, projectId]), so the star reflects instantly wherever it is
// rendered. Rolls back both on error.
export const useToggleProjectFavorite = () => {
  const queryClient = useQueryClient();
  const { userId } = useAppSelector((state: RootState) => state.user);
  const projectsKey = [PROJECTS_QUERY_KEY, userId];

  return useMutation({
    mutationKey: ['toggle-project-favorite'],
    mutationFn: (projectId: string) => projectService.toggleProjectFavorite(projectId),
    onMutate: async (projectId: string) => {
      const projectKey = [PROJECT_QUERY_KEY, projectId];
      await queryClient.cancelQueries({ queryKey: projectsKey });
      await queryClient.cancelQueries({ queryKey: projectKey });

      const previous = queryClient.getQueryData<Project[]>(projectsKey);
      const previousProject = queryClient.getQueryData<Project>(projectKey);

      queryClient.setQueryData<Project[]>(projectsKey, (old) =>
        (old || []).map((project) =>
          project.projectId === projectId
            ? { ...project, isFavorited: !project.isFavorited }
            : project,
        ),
      );

      queryClient.setQueryData<Project>(projectKey, (old) =>
        old ? { ...old, isFavorited: !old.isFavorited } : old,
      );

      return { previous, previousProject };
    },
    onError: (error: any, projectId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(projectsKey, context.previous);
      }
      if (context?.previousProject) {
        queryClient.setQueryData([PROJECT_QUERY_KEY, projectId], context.previousProject);
      }
      const errorMessage =
        error?.response?.errors?.[0]?.message ||
        error?.message ||
        'Failed to update favorite';
      toast.error(errorMessage);
    },
    onSettled: (_data, _error, projectId) => {
      queryClient.invalidateQueries({ queryKey: projectsKey });
      queryClient.invalidateQueries({ queryKey: [PROJECT_QUERY_KEY, projectId] });
    },
  });
};

export default useToggleProjectFavorite;
